import { DISPLAY_MOMENT_TIME_HM_AM_PM, DISPLAY_MOMENT_TIME_HMS, PICK_UP_PRICE } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { PICK_UP_PRICE_TYPE, SHOP_SERVICE_TYPE } from '@/types/enums';
import { ShopService } from '@/types/shop';
import { formatDateTime } from '@/utils/datetime';
import { convertServicePrice, convertServiceList } from '@/utils/utils';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export default observer(function ServiceList() {
    // hooks
    const { t } = useTranslation();
    // store
    const { appointmentStore: { appointment } } = useStore();
    const selectedServices = appointment?.selectedServices || [];

    // function
    // functions
    const medicalServices = useMemo<ShopService[]>(() => {
        if (selectedServices && selectedServices.length > 0) {
            return selectedServices
                .filter(service => service.type === SHOP_SERVICE_TYPE.Medical || service.isMedicalService)
                .sort((a, b) => a.type - b.type);
        }
        return [];
    }, [selectedServices]);

    const groomingServices = useMemo<ShopService[]>(() => {
        if (selectedServices && selectedServices.length > 0) {
            return selectedServices
                .filter(service => service.type !== SHOP_SERVICE_TYPE.Medical && !service.isMedicalService)
                .sort((a, b) => a.type - b.type);
        }
        return [];
    }, [selectedServices]);

    return (
        <div className='p-2 bg-white rounded'>
            <h3 className='font-semibold text-active text-[0.9375rem]'>{t('words_title.service_list')}</h3>
            {appointment?.pickUpService && (
                <div className='mr-4 mt-3 border-b border-b-border-8 pb-4 flex flex-col gap-3'>
                    <div className='flex justify-between'>
                        <h4 className='font-semibold'>{t('words_title.pick_up_service')}</h4>
                        <span>
                            {
                                appointment.pickUpPriceType === PICK_UP_PRICE_TYPE.Free &&
                                t(`select_options.${PICK_UP_PRICE[PICK_UP_PRICE_TYPE.Free].key}`)
                            }
                            {
                                appointment.pickUpPriceType === PICK_UP_PRICE_TYPE.ChargeByDistance &&
                                (appointment.pickUpPrice ? convertServicePrice(appointment?.pickUpPrice, t) : '-- VND')
                            }
                            {/* {convertServicePrice(appointment?.pickUpPrice, t)} */}
                        </span>
                    </div>
                    <div className='border border-border-2 rounded-md p-4 flex flex-col gap-3'>
                        <div className='grid grid-cols-[14rem,1fr]'>
                            <label className='text-text-4'>{`${t('words_title.pick_up_time')} (${t('words_title.planned')})`}</label>
                            <p>
                                {formatDateTime(appointment?.pickUpTime, DISPLAY_MOMENT_TIME_HM_AM_PM, {
                                    onlyTime: true,
                                    currentformat: DISPLAY_MOMENT_TIME_HMS
                                }) || '--'}
                            </p>
                        </div>
                        <div className='grid grid-cols-[14rem,1fr]'>
                            <label className='text-text-4'>{t('words_title.pick_up_address')}</label>
                            <p className='whitespace-pre-line'>{appointment?.pickUpAddress || '--'}</p>
                        </div>
                        <div className='grid grid-cols-[14rem,1fr]'>
                            <label className='text-text-4'>{t('words_title.contact')}</label>
                            <p>{appointment?.contact || '--'}</p>
                        </div>
                    </div>
                </div>
            )}
            {groomingServices && groomingServices.length > 0 && (
                <div className={classNames('mt-3', medicalServices && medicalServices.length > 0 && 'border-b border-b-border-8')}>
                    <h4 className='font-semibold'>{t('words_title.grooming_service')}</h4>
                    <div className='flex flex-col gap-3 px-4 py-2'>
                        {convertServiceList(groomingServices, t).map((service, index) => {
                            return (
                                <div
                                    key={index}
                                    className={classNames(
                                        'pb-3 border-b border-dashed border-b-border-8 flex justify-between gap-2',
                                        { 'border-none': index === groomingServices.length - 1 }
                                    )}
                                >
                                    <p className='text-tab-1 w-3/4'>{service.serviceName}</p>
                                    <p className='text-tab-1 w-1/4 text-right'>
                                        {service.servicePrice}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {medicalServices && medicalServices.length > 0 && (
                <div className='mt-3'>
                    <h4 className='font-semibold'>{t('words_title.medical_service')}</h4>
                    <div className='flex flex-col gap-3 px-4 py-2'>
                        {convertServiceList(medicalServices, t).map((service, index) => {
                            return (
                                <div
                                    key={index}
                                    className={classNames(
                                        'pb-3 border-b border-dashed border-b-border-8 flex justify-between gap-2',
                                        { 'border-none': index === medicalServices.length - 1 }
                                    )}
                                >
                                    <p className='text-tab-1 w-3/4'>{service.serviceName}</p>
                                    <p className='text-tab-1 w-1/4 text-right'>
                                        {service.servicePrice}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
});
